// =============================================================================
// OELOX — Shared Utilities
// =============================================================================

export { APP_NAME, APP_TAGLINE } from "./brand";
export { migrateLocalStorageKey } from "./storage";
export * from "./customers";
export * from "./menu";
export * from "./loyalty";
export * from "./drivers";
export {
  ORDERS_PAGE_SIZE,
  ORDER_DESK_STEPS,
  PREP_TIME_OPTIONS,
  statusToDeskStep,
  deskStepToStatuses,
  statusToPipelineStage,
  primaryActionForStep,
  nextStatusAfterAction,
  waitingMinutesSince,
  isOrderStalled,
  mapOrderRow,
  mapOrderLine,
  validateCreateStaffOrder,
  computeOrderTotals,
  type OrderDbStatus,
  type OrderDeskStep,
  type OrderDeskFilter,
  type OrderChannel,
  type OrderFulfillment,
  type OrderPaymentMethod,
  type OrderPipelineStage,
  type OrderPrimaryAction,
  type OrderLineRecord,
  type OrderRecord,
  type OrderDetailRecord,
  type OrderDeskStats,
  type CreateOrderLineInput,
  type CreateStaffOrderInput,
} from "./orders";

// ── Currency ─────────────────────────────────────────────────────────────────

const currencyToLocale: Record<string, string> = {
  MYR: "en-MY",
  IQD: "ar-IQ",
  USD: "en-US",
  AED: "ar-AE",
  SAR: "ar-SA",
  EGP: "ar-EG",
  IDR: "id-ID",
  GBP: "en-GB",
  INR: "en-IN",
  THB: "th-TH"
};

const currencyFormatters = new Map<string, Intl.NumberFormat>();

export function formatCurrency(amount: number, currency = "USD", locale?: string): string {
  const resolvedLocale = locale || currencyToLocale[currency] || "en-US";
  const key = `${currency}_${resolvedLocale}`;
  if (!currencyFormatters.has(key)) {
    currencyFormatters.set(
      key,
      new Intl.NumberFormat(resolvedLocale, {
        style: "currency",
        currency,
        minimumFractionDigits: currency === "IQD" ? 0 : 2,
        maximumFractionDigits: currency === "IQD" ? 0 : 2,
      })
    );
  }
  return currencyFormatters.get(key)!.format(amount);
}

export function formatCompactCurrency(amount: number, currency = "USD", locale?: string): string {
  const resolvedLocale = locale || currencyToLocale[currency] || "en-US";
  return new Intl.NumberFormat(resolvedLocale, {
    style: "currency",
    currency,
    notation: "compact",
    minimumFractionDigits: currency === "IQD" ? 0 : 1,
    maximumFractionDigits: currency === "IQD" ? 0 : 1,
  }).format(amount);
}

// ── Numbers ──────────────────────────────────────────────────────────────────

export function formatNumber(n: number): string {
  return new Intl.NumberFormat("en-US").format(n);
}

export function formatPercent(value: number, decimals = 1): string {
  return `${(value * 100).toFixed(decimals)}%`;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

// ── Distance ─────────────────────────────────────────────────────────────────

/**
 * Haversine formula — distance in km between two lat/lng points.
 */
export function haversineDistance(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 6371; // Earth radius in km
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

export function formatDistance(km: number): string {
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(1)} km`;
}

export function formatETA(minutes: number): string {
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

// ── Time & Date ──────────────────────────────────────────────────────────────

export function formatDateTime(
  value: string | Date | number,
  timezone: string,
  locale = "en-US"
): string {
  try {
    const date = typeof value === "string" || typeof value === "number" ? new Date(value) : value;
    return new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: timezone,
    }).format(date);
  } catch {
    return new Date(value).toLocaleString();
  }
}

export function timeAgo(dateString: string): string {
  const now = Date.now();
  const then = new Date(dateString).getTime();
  const seconds = Math.floor((now - then) / 1000);

  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

export function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function formatTime(dateString: string): string {
  return new Date(dateString).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ── IDs & Tokens ─────────────────────────────────────────────────────────────

export function generateId(prefix = ""): string {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  let id = "";
  for (let i = 0; i < 16; i++) {
    id += chars[Math.floor(Math.random() * chars.length)];
  }
  return prefix ? `${prefix}_${id}` : id;
}

export function generateTrackingToken(): string {
  return `vt_${generateId()}`;
}

// ── Validation ───────────────────────────────────────────────────────────────

export function isValidPhone(phone: string): boolean {
  return /^\+?[\d\s\-()]{7,20}$/.test(phone);
}

export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

// ── Color / Status helpers ───────────────────────────────────────────────────

export function orderStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    draft: "Draft",
    pending_cashier_approval: "Pending Approval",
    approved: "Approved",
    in_kitchen: "In Kitchen",
    ready_for_dispatch: "Ready for Dispatch",
    blasted_to_drivers: "Finding Driver",
    assigned: "Driver Assigned",
    picked_up: "Picked Up",
    out_for_delivery: "Out for Delivery",
    delivered: "Delivered",
    completed: "Completed",
    cancelled: "Cancelled",
  };
  return labels[status] ?? status;
}

export function orderStatusColor(status: string): string {
  const colors: Record<string, string> = {
    draft: "info",
    pending_cashier_approval: "warning",
    approved: "success",
    in_kitchen: "info",
    ready_for_dispatch: "accent",
    blasted_to_drivers: "accent",
    assigned: "success",
    picked_up: "success",
    out_for_delivery: "accent",
    delivered: "success",
    completed: "success",
    cancelled: "danger",
  };
  return colors[status] ?? "info";
}

// ── Misc ─────────────────────────────────────────────────────────────────────

export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength - 3) + "...";
}

export function pluralize(count: number, singular: string, plural?: string): string {
  return count === 1 ? singular : (plural ?? `${singular}s`);
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function debounce<T extends (...args: unknown[]) => void>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}
